import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { MARKET_PREFERENCE_KEY, BANNER_HIDDEN_KEY, countryCodeToMarket } from '../../util/markets';
import { useConfiguration } from '../../context/configurationContext';

import css from './CountryFilterBanner.module.css';

/**
 * Banner component that displays location-specific messaging based on a user's country.
 * Uses Cloudflare's CF-IPCountry header with browser language as fallback.
 */
const CountryFilterBanner = props => {
  const { onActionClick, persistClosed = true } = props;

  const [isVisible, setIsVisible] = useState(true);
  const [userCountry, setUserCountry] = useState(null);
  const intl = useIntl();
  const config = useConfiguration();

  useEffect(() => {
    if (persistClosed && localStorage.getItem(BANNER_HIDDEN_KEY) === 'true') {
      setIsVisible(false);
    }
  }, [persistClosed]);

  useEffect(() => {
    // [Country Filter Step 7] Use Cloudflare country from config
    if (config?.cloudflareCountry) {
      setUserCountry(config.cloudflareCountry);
      return;
    }
    // Fallback to browser language detection
    const detectCountryFromBrowser = () => {
      const browserLanguages = navigator.languages || [navigator.language];
      if (!browserLanguages || browserLanguages.length === 0) {
        return null;
      }

      for (const locale of browserLanguages) {
        if (!locale) continue;
        const parts = locale.split(/[-_]/);
        if (parts.length >= 2 && parts[1].length === 2) {
          const country = parts[1].toUpperCase();
          return country;
        }
      }
      return null;
    };

    const browserCountry = detectCountryFromBrowser();
    setUserCountry(browserCountry);
  }, [config]);

  useEffect(() => {
    if (isVisible && userCountry) {
      if (localStorage.getItem(MARKET_PREFERENCE_KEY)) {
        return;
      }
      const userMarket = countryCodeToMarket(userCountry);
      if (userMarket !== 'UK' && userMarket !== 'EU') {
        return;
      }
      if (typeof _paq !== 'undefined') {
        _paq.push(['trackEvent', 'Market', 'MarketFilterShow', userMarket]);
      }
    }
  }, [isVisible, userCountry]);

  const handleClose = () => {
    if (typeof _paq !== 'undefined') {
      const userMarket = countryCodeToMarket(userCountry);
      _paq.push(['trackEvent', 'Market', 'MarketFilterClose', userMarket]);
    }

    setIsVisible(false);

    if (persistClosed) {
      try {
        localStorage.setItem(BANNER_HIDDEN_KEY, 'true');
      } catch (error) {
        console.error('Error saving banner state:', error);
      }
    }
  };

  const handleActionClick = () => {
    const marketPreference = countryCodeToMarket(userCountry);

    if (typeof _paq !== 'undefined') {
      _paq.push(['trackEvent', 'Market', 'MarketFilterClick', marketPreference]);
    }

    try {
      localStorage.setItem(MARKET_PREFERENCE_KEY, marketPreference);
      localStorage.setItem(BANNER_HIDDEN_KEY, 'true');
    } catch (error) {
      console.error('Error saving preferences:', error);
    }

    setIsVisible(false);
    onActionClick(marketPreference);
  };

  if (!isVisible || !userCountry) {
    return null;
  }

  if (localStorage.getItem(MARKET_PREFERENCE_KEY)) {
    return null;
  }

  const userMarket = countryCodeToMarket(userCountry);

  if (userMarket !== 'UK' && userMarket !== 'EU') {
    return null;
  }

  const messageId =
    userMarket === 'UK' ? 'CountryFilterBanner.message' : 'CountryFilterBanner.messageEU';

  const buttonId =
    userMarket === 'UK'
      ? 'CountryFilterBanner.viewEligibleItemsButton'
      : 'CountryFilterBanner.viewEligibleItemsButtonEU';

  const closeButtonText = intl.formatMessage({ id: 'CountryFilterBanner.closeButtonText' });

  return (
    <div className={css.container}>
      <div className={css.placeholderLeft}></div>

      <div className={css.contentWrapper}>
        <div className={css.messageText}>
          <FormattedMessage id={messageId} />
        </div>

        <button className={css.actionButton} onClick={handleActionClick}>
          <span className={css.actionButtonText}>
            <FormattedMessage id={buttonId} />
          </span>
        </button>
      </div>

      <button className={css.closeButton} onClick={handleClose} aria-label={closeButtonText}>
        <div className={css.closeIcon}></div>
      </button>
    </div>
  );
};

export default CountryFilterBanner;
